<template>
  <div class="flex flex-col gap-3">
    <!-- Not tested (or the mail changed since): the test is what comes next -->
    <div v-if="!done || !sentTo" class="flex flex-wrap items-center gap-3">
      <Button :label="t('mail_campaign_test_send')" :severity="optional || !templateId ? 'secondary' : undefined" :loading="testing === 'me'"
        :disabled="!templateId || !!testing" @click="send()">
        <template #icon><AppIcon icon="paper-plane" class="mr-2" /></template>
      </Button>
      <span class="text-sm text-surface-600 dark:text-surface-300">{{ t('mail_campaign_test_why') }}</span>
    </div>
    <div v-else class="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm" aria-live="polite">
      <span class="cut-sm grid place-items-center size-6 shrink-0 bg-mint text-ink" style="--chamfer: 5px" aria-hidden="true"><AppIcon icon="check" /></span>
      <span>{{ t('mail_campaign_test_sent', { to: sentTo }) }}</span>
      <button type="button" :class="link" :disabled="!!testing" @click="send()">{{ t('mail_campaign_test_again') }}</button>
    </div>
    <p v-if="colleagueSent" class="m-0 text-sm" aria-live="polite">{{ t('mail_campaign_test_sent', { to: colleagueSent }) }}</p>
    <button v-if="!colleagueOpen && colleagues.length" type="button" :class="link" class="self-start" @click="colleagueOpen = true">
      {{ t('mail_campaign_test_also_colleague') }}
    </button>
    <div v-if="colleagueOpen" class="flex flex-wrap items-center gap-2">
      <Select v-model="colleague" :options="colleagues" :placeholder="t('mail_campaign_test_colleague_pick')"
        :aria-label="t('mail_campaign_test_colleague_pick')" class="w-full sm:w-72" />
      <Button :label="t('mail_campaign_test_colleague_send')" severity="secondary" :disabled="!colleague || !templateId || !!testing"
        :loading="testing === 'colleague'" @click="send(colleague!)" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n';

// A test of the stored mail (template_id), recorded per version on the server (mail_template_tests).
const props = defineProps<{
  templateId: string | null; kind: 'marketing' | 'transactional'; lang?: string; eventId?: string | null;
  done?: boolean; sentTo?: string | null; optional?: boolean;
}>();
const emit = defineEmits<{ sent: [{ sent_to: string; template_updated_at: string | null }] }>();

const { t } = useI18n();
const toast = useToast();
const { action, loadTeamEmails } = useMail();

const colleagues = ref<string[]>([]);
onMounted(async () => { colleagues.value = (await loadTeamEmails()).slice(1); });
const colleagueOpen = ref(false);
const colleague = ref<string | null>(null);
const colleagueSent = ref<string | null>(null);
const link = 'p-0 border-0 bg-transparent text-sm font-semibold underline underline-offset-4 cursor-pointer text-ink dark:text-surface-0 disabled:opacity-60';

const testing = ref<'me' | 'colleague' | null>(null);
const send = async (to?: string) => {
  if (!props.templateId) return;
  testing.value = to ? 'colleague' : 'me';
  try {
    const result = await action<{ sent_to: string; template_updated_at: string | null }>('test', {
      template_id: props.templateId, kind: props.kind, lang: props.lang, event_id: props.eventId ?? undefined, ...(to ? { to } : {}),
    });
    emit('sent', result);
    // a colleague's test says so on its own line; your own is the done line above
    if (to) colleagueSent.value = result.sent_to;
  } catch (e) {
    const code = e instanceof Error ? e.message : '';
    showSupabaseError(toast, e, {
      summary: t('mail_campaign_test_failed'),
      detail: code === 'not_a_team_member' ? t('mail_campaign_test_not_team') : undefined,
      life: 6000,
    });
  } finally {
    testing.value = null;
  }
};
</script>
